class FamilySizePicker extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.radios = Array.from(this.querySelectorAll('.wm-family-sizes input.js-option'));
    this.styleButtons = Array.from(this.querySelectorAll('[data-family-style]'));
    this.priceRecords = JSON.parse(this.querySelector('[data-family-variant-prices]')?.textContent || '{}');
    this.handleStyleClick = this.onStyleClick.bind(this);
    this.handleSizeChange = this.onSizeChange.bind(this);
    this.handleVariantChange = () => {
      this.syncStyle();
      this.applyImmediateVariant();
    };
    this.addEventListener('click', this.handleStyleClick);
    this.addEventListener('change', this.handleSizeChange);
    this.variantPicker = this.closest('variant-picker');
    this.variantPicker?.addEventListener('on:variant:change', this.handleVariantChange);
    this.radios.forEach((radio) => {
      const text = radio.nextElementSibling?.querySelector('.js-value');
      if (text) text.textContent = radio.dataset.familySizeLabel;
    });
    this.classList.add('is-enhanced');
    this.querySelector('.wm-family-styles').hidden = false;
    this.syncStyle();
    this.applyImmediateVariant();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleStyleClick);
    this.removeEventListener('change', this.handleSizeChange);
    this.variantPicker?.removeEventListener('on:variant:change', this.handleVariantChange);
    this.initialized = false;
  }

  syncStyle() {
    const selected = this.radios.find((radio) => radio.checked);
    this.activeStyle = selected?.dataset.familyGroup || this.activeStyle || this.styleButtons[0]?.dataset.familyStyle;
    this.styleButtons.forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.familyStyle === this.activeStyle));
    });
    this.radios.forEach((radio) => {
      const hidden = radio.dataset.familyGroup !== this.activeStyle;
      radio.hidden = hidden;
      if (radio.nextElementSibling) radio.nextElementSibling.hidden = hidden;
    });
  }

  onSizeChange(event) {
    if (this.radios.includes(event.target)) {
      const picker = this.variantPicker;
      const requestId = picker?.variantRequestId;
      const radio = event.target;
      queueMicrotask(() => {
        if (this.isConnected && radio.checked && picker?.isConnected
            && typeof requestId === 'number' && picker.variantRequestId === requestId
            && typeof picker.handleVariantChange === 'function') {
          picker.handleVariantChange(event);
        }
      });
      this.syncStyle();
      this.applyImmediateVariant();
    }
  }

  applyImmediateVariant() {
    const selected = this.radios.find((radio) => radio.checked);
    const record = this.priceRecords[selected?.dataset.variantId];
    const scope = this.variantPicker?.section || this.closest('.product-detail');
    if (!record || !scope) return;
    scope.querySelectorAll('.price-area[data-dynamic-variant-content]').forEach((area) => {
      if (area.dataset.familyPriceVariant === String(record.id)) return;
      area.innerHTML = record.price_html;
      area.dataset.familyPriceVariant = String(record.id);
    });
    scope.querySelectorAll('input[name="id"][data-dynamic-variant-content]').forEach((input) => {
      input.value = String(record.id);
      input.setAttribute('value', String(record.id));
    });
    scope.querySelectorAll('.js-product-form button[name="add"]').forEach((button) => {
      button.disabled = !record.available;
      button.textContent = record.available ? button.dataset.addToCartText : theme.strings.noStock;
    });
    scope.querySelectorAll('.sku').forEach((container) => {
      container.classList.toggle('sku--no-sku', !record.sku);
      const value = container.querySelector('.sku__value');
      if (value) value.textContent = record.sku || '';
    });
  }

  onStyleClick(event) {
    const button = event.target.closest('[data-family-style]');
    if (!button || !this.contains(button)) return;
    const style = button.dataset.familyStyle;
    const selected = this.radios.find((radio) => radio.checked);
    if (style === this.activeStyle && selected) return;
    const size = selected?.dataset.familySizeLabel?.toLowerCase();
    const choices = this.radios.filter((radio) => radio.dataset.familyGroup === style);
    const available = choices.filter((radio) => !radio.classList.contains('is-unavailable'));
    const next = available.find((radio) => radio.dataset.familySizeLabel.toLowerCase() === size)
      || available[0]
      || choices.find((radio) => radio.dataset.familySizeLabel.toLowerCase() === size)
      || choices[0];
    if (!next) return;
    this.activeStyle = style;
    next.click();
    this.syncStyle();
  }
}

if (!customElements.get('family-size-picker')) {
  customElements.define('family-size-picker', FamilySizePicker);
}

(() => {
  let activeChart;
  const focusClose = () => activeChart?.modal.querySelector('.cc-modal-window__close')?.focus();
  const isOpen = () => activeChart?.modal.isConnected
    && !activeChart.modal.classList.contains('cc-modal-window--closing');

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('.size-chart-link.cc-modal');
    if (opener) {
      const modals = document.querySelectorAll('.cc-modal-window:not(.cc-modal-window--closing)');
      const modal = modals[modals.length - 1];
      if (!modal) return;
      activeChart = { modal, opener };
      modal.querySelector('[role="dialog"]')?.setAttribute('aria-label', opener.textContent.trim());
      return;
    }
    if (activeChart?.modal.contains(event.target)
        && event.target.closest('.cc-modal-window__close, .cc-modal-window__background')) {
      const opener = activeChart.opener;
      activeChart = undefined;
      setTimeout(() => { if (opener.isConnected) opener.focus(); }, 20);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!isOpen()) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      activeChart.modal.querySelector('.cc-modal-window__close')?.click();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(activeChart.modal.querySelectorAll(
      'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((element) => !element.closest('[hidden], [aria-hidden="true"]'));
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first) {
      event.preventDefault();
      return;
    }
    if (event.shiftKey && (document.activeElement === first || !activeChart.modal.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !activeChart.modal.contains(document.activeElement))) {
      event.preventDefault();
      first.focus();
    }
  }, true);

  document.addEventListener('focusin', (event) => {
    if (isOpen() && !activeChart.modal.contains(event.target)) focusClose();
  });
})();
